"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import SuperAdminSecurityModal from "./SuperAdminSecurityModal";
import { 
  verifyAdminLoginCode, 
  requestAdminLoginCode,
  getErrorMessage, 
  isCredentialsError, 
  isAccountLockedError 
} from "../utils/adminSignInApi";

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  role: string;
  password?: string; // For admin roles
  onSuccess?: () => void;
  onResendCode?: () => void;
}

export default function VerificationModal({ 
  isOpen, 
  onClose, 
  email, 
  role, 
  password,
  onSuccess, 
  onResendCode 
}: VerificationModalProps) {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showSuperAdminSecurity, setShowSuperAdminSecurity] = useState(false);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Check if current role requires admin API
  const isAdminRole = role === "admin" || role === "super-admin";

  // Detect mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Start countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setResendCountdown(60); // 60 second countdown
      // Focus first input
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (index: number, value: string) => {
    // Handle multiple characters (paste scenario)
    if (value.length > 1) {
      const pastedData = isAdminRole 
        ? value.toUpperCase().replace(/[^A-Z0-9]/g, '')
        : value.replace(/\D/g, '');
      
      if (pastedData.length > 0) {
        const newCode = [...code];
        const remainingSlots = 6 - index;
        const charsToProcess = pastedData.slice(0, remainingSlots);
        
        charsToProcess.split('').forEach((char, i) => {
          if (index + i < 6) {
            newCode[index + i] = char;
          }
        });
        
        setCode(newCode);
        setError(null);
        
        // Focus next empty input or last filled input
        const nextIndex = Math.min(index + charsToProcess.length, 5);
        inputRefs.current[nextIndex]?.focus();
        
        // Auto-verify if all fields are filled
        if (newCode.every(digit => digit !== "")) {
          handleVerify(newCode);
        }
      }
      return;
    }

    // Handle single character input
    const sanitizedValue = isAdminRole 
      ? value.toUpperCase().replace(/[^A-Z0-9]/g, '')
      : value.replace(/\D/g, '');
    
    const newCode = [...code];
    newCode[index] = sanitizedValue;
    setCode(newCode);
    setError(null);

    // Auto-move to next input
    if (sanitizedValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all digits are entered
    if (newCode.every(digit => digit !== "")) {
      handleVerify(newCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    // Handle both numeric and alphanumeric codes
    const cleanedData = isAdminRole 
      ? pastedData.toUpperCase().replace(/[^A-Z0-9]/g, '')
      : pastedData.replace(/\D/g, '');
    
    if (cleanedData.length >= 6) {
      const newCode = cleanedData.slice(0, 6).split('');
      setCode(newCode);
      setError(null);
      
      // Focus the last input
      inputRefs.current[5]?.focus();
      
      // Auto-verify
      handleVerify(newCode);
    } else if (cleanedData.length > 0) {
      // Partial paste - fill from current position
      const currentIndex = code.findIndex(digit => digit === "") || 0;
      const newCode = [...code];
      
      cleanedData.split('').forEach((char, i) => {
        if (currentIndex + i < 6) {
          newCode[currentIndex + i] = char;
        }
      });
      
      setCode(newCode);
      setError(null);
      
      // Focus next empty input
      const nextEmptyIndex = newCode.findIndex(digit => digit === "");
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[5]?.focus();
        // Auto-verify if all filled
        if (newCode.every(digit => digit !== "")) {
          handleVerify(newCode);
        }
      }
    }
  };

  const handleVerify = async (codeToVerify: string[] = code) => {
    const verificationCode = codeToVerify.join('');
    
    if (verificationCode.length !== 6) {
      setError("Please enter the complete 6-character code");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      if (isAdminRole && password) {
        // Admin verification flow - use cookie-based API
        const response = await verifyAdminLoginCode(email, verificationCode);
        
        console.log("Admin verification successful:", response);
        
        // Cookies are set automatically by the server
        // No need to store tokens manually anymore!
        
        // Close modal and redirect
        onSuccess?.();
        onClose();
        
        // Redirect to admin dashboard
        const dashboardUrl = "/admin";
        window.location.href = dashboardUrl;
      } else {
        // Regular user flow - simulate verification
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // For demo - accept "123456" as valid code for regular users
        if (verificationCode === "123456") {
          console.log("Regular user verification successful for:", email, "Role:", role);
          onSuccess?.();
          onClose();
        } else {
          setError("Invalid verification code. Please try again.");
          setCode(["", "", "", "", "", ""]);
          inputRefs.current[0]?.focus();
        }
      }
    } catch (error) {
      console.error("Verification error:", error);
      
      // Handle specific error types for admin verification
      if (isAdminRole) {
        if (isCredentialsError(error)) {
          setError("Invalid verification code. Please check the code and try again.");
        } else if (isAccountLockedError(error)) {
          setError(getErrorMessage(error)); // Show specific lockout message
        } else {
          setError(getErrorMessage(error));
        }
      } else {
        setError("Verification failed. Please try again.");
      }
      
      // Clear code and focus first input
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    
    setError(null);
    setResendCountdown(60);
    
    try {
      if (isAdminRole && password) {
        // Admin role - use real API to resend code
        await requestAdminLoginCode(email, password);
        console.log("Admin verification code resent to:", email);
      } else {
        // Regular user - use provided callback or simulate
        if (onResendCode) {
          await onResendCode();
        } else {
          // Simulate resend for regular users
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        console.log("Verification code resent to:", email);
      }
    } catch (error) {
      console.error("Resend error:", error);
      
      if (isAdminRole) {
        if (isCredentialsError(error)) {
          setError("Unable to resend code. Please check your credentials.");
        } else if (isAccountLockedError(error)) {
          setError(getErrorMessage(error));
        } else {
          setError(getErrorMessage(error));
        }
      } else {
        setError("Failed to resend code. Please try again.");
      }
      
      setResendCountdown(0);
    }
  };

  const handleSuperAdminSecurityClose = () => {
    setShowSuperAdminSecurity(false);
    onClose();
  };

  const handleSuperAdminSecuritySuccess = () => {
    console.log("Super Admin security verification completed successfully");
    setShowSuperAdminSecurity(false);
    onSuccess?.();
    onClose();
  };

  const getRoleDisplayName = (roleValue: string) => {
    const roleMap: { [key: string]: string } = {
      'predictor': 'Predictor',
      'admin': 'Admin',
      'super-admin': 'Super Admin'
    };
    return roleMap[roleValue] || roleValue;
  };

  // If showing super admin security, render that modal
  if (showSuperAdminSecurity) {
    return (
      <SuperAdminSecurityModal
        isOpen={showSuperAdminSecurity}
        onClose={handleSuperAdminSecurityClose}
        email={email}
        onSuccess={handleSuperAdminSecuritySuccess}
      />
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className={`fixed z-50 pointer-events-none ${
        isMobile 
          ? 'inset-x-0 top-[77px] flex justify-center' 
          : 'left-1/2 top-[77px] transform -translate-x-1/2'
      }`}>
        <div 
          className={`bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f1419] rounded-2xl border border-[#CB6CE6]/20 pointer-events-auto relative shadow-2xl shadow-[#CB6CE6]/10 ${
            isMobile 
              ? 'w-[90%] max-h-[80vh] overflow-y-auto' 
              : 'w-[420px]'
          }`} 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-300 z-10 group"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-[#CB6CE6] transition-colors"/>
            </svg>
          </button>
          
          <div className={`relative ${isMobile ? 'px-6 py-6' : 'px-8 py-8'}`}>
            {/* Error message */}
            {error && (
              <div className="absolute top-0 left-0 right-0 flex justify-center">
                <div className="bg-red-900/30 border border-red-500/50 rounded-full px-4 py-2 flex items-center space-x-2 shadow-lg translate-y-[-50%] max-w-[90%] backdrop-blur-sm">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 4V9" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 11.5V12" stroke="#ef4444" strokeWidth="1.5" strokeLinejoin="round"/>
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-red-400 text-sm font-medium">{error}</span>
                </div>
              </div>
            )}
            
            {/* Logo and title in one line */}
            <div className="relative mb-4 flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] rounded-lg blur-xl opacity-20 w-10 h-10"></div>
                <Image
                  src="/phoebe-logo.png"
                  alt="Phoebe Logo"
                  width={32}
                  height={32}
                  className="object-contain relative z-10 drop-shadow-lg"
                />
              </div>
              <div>
                <h2 className={`font-black text-transparent bg-clip-text bg-gradient-to-r from-[#CB6CE6] via-white to-[#6B21A8] ${
                  isMobile ? 'text-xl' : 'text-2xl'
                }`}>
                  Verify Access
                </h2>
                <p className="text-[#9CA3AF] text-sm">
                  Enter the {isAdminRole ? '6-character' : '6-digit'} code sent to your email
                </p>
              </div>
            </div>

            {/* Email info */}
            <div className="bg-black/20 rounded-lg p-3 border border-[#CB6CE6]/20 mb-5">
              <div className="text-white font-medium text-sm">{email}</div>
              <div className="text-[#CB6CE6] text-xs mt-1">Role: {getRoleDisplayName(role)}</div>
            </div>

            {/* Verification Code Input */}
            <div className="mb-5">
              <label className="block text-white font-medium mb-3 text-sm">
                Verification Code
              </label>
              
              <div className="flex justify-center space-x-2 mb-4">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode={isAdminRole ? "text" : "numeric"}
                    pattern={isAdminRole ? "[A-Z0-9]*" : "[0-9]*"}
                    maxLength={6} // Allow longer input for paste handling
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`w-10 h-12 text-center text-lg font-bold bg-black/30 border border-[#CB6CE6]/30 rounded-xl text-white hover:border-[#CB6CE6]/50 focus:border-[#CB6CE6] focus:outline-none transition-all duration-300 backdrop-blur-sm ${
                      digit ? 'border-[#CB6CE6] bg-[#CB6CE6]/10' : ''
                    }`}
                    disabled={isVerifying}
                    placeholder={isAdminRole ? "●" : "0"}
                  />
                ))}
              </div>

              {/* Resend Code */}
              <div className="text-center">
                <p className="text-[#9CA3AF] text-xs mb-1">
                  Didn't receive the code?
                </p>
                <button
                  onClick={handleResendCode}
                  disabled={resendCountdown > 0}
                  className={`text-xs font-medium transition-colors ${
                    resendCountdown > 0
                      ? 'text-[#9CA3AF] cursor-not-allowed'
                      : 'text-[#CB6CE6] hover:text-[#6B21A8] cursor-pointer'
                  }`}
                >
                  {resendCountdown > 0 
                    ? `Resend in ${resendCountdown}s` 
                    : 'Resend Code'
                  }
                </button>
              </div>
            </div>

            {/* Verify Button */}
            <button
              onClick={() => handleVerify()}
              disabled={isVerifying || code.some(digit => !digit)}
              className="w-full bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] text-white py-3 rounded-xl font-bold text-base hover:shadow-2xl hover:shadow-[#CB6CE6]/30 transform hover:scale-[1.02] transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
            >
              {isVerifying ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Verifying...
                </div>
              ) : (
                <span className="relative z-10">Verify & Continue</span>
              )}
              
              {/* Button shine effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-out"></div>
            </button>
            
            {/* Back to login */}
            <div className="mt-4 text-center">
              <button 
                onClick={onClose}
                className="text-[#9CA3AF] hover:text-white text-xs transition-colors"
              >
                ← Back to login
              </button>
            </div>
            
            {/* Cancel button for mobile */}
            {isMobile && (
              <button 
                className="mt-3 w-full py-2 text-[#9CA3AF] text-sm font-medium border border-white/10 rounded-xl hover:bg-white/5 transition-all duration-300"
                onClick={onClose}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}